import Decimal from 'decimal.js';
import { roundTo3Decimals } from 'src/utils/numberHelper';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { UUID } from 'crypto';
import { getCargoOperationStowedTankIds, notStowedWeightInTanks } from './stowageOperationUtils';
import { getShipFigureForOperationType } from './operationDisplayUtil';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import { PortName } from 'src/types/ViewModel/Stowage/Port/PortName';

/**
 * Simplified view model for cargo operations display
 */
export interface RotationCargoOperationVM {
    cargoOperationGuid: UUID;
    portCallGuid?: UUID;
    berthCallGuid?: UUID;
    cargo: RotationCargo;
    portDisplayName: string;
    berthName?: string;
    cargoOperationSequenceNumber?: number;
    cargoOperationOrderNumber?: number;
    nominatedWeight?: number;
    billOfLadingWeight?: number;
    operationType: string;
    stowedWeight?: number;
    stowedVolume?: number;
    optionType?: string;
    optionMinusPercentage?: number;
    optionPlusPercentage?: number;
    orgCargoOperation?: RotationCargoOperation;
}

/**
 * Operation with calculated location and stowage details
 */
export interface OperationWithLocation {
    operation: RotationCargoOperationVM;
    portDisplayName: string;
    berthName: string;
    stowedWeight: Decimal;
    stowedVolume: Decimal;
    notStowedWeight: Decimal | null;
    notStowedVolume: Decimal | null;
}

/**
 * Format nominated weight for display
 * @param operation - The cargo operation view model
 * @returns Formatted weight string with MT unit or 'N/A'
 */
export const formatNominatedWeightDisplay = (operation: RotationCargoOperationVM): string => {
    return operation.nominatedWeight ? `${roundTo3Decimals(operation.nominatedWeight)} MT` : 'N/A';
};

/**
 * Format bill of lading weight for display
 * @param operation - The cargo operation view model
 * @returns Formatted weight string with MT unit or '-'
 */
export const formatBillOfLadingWeightDisplay = (operation: RotationCargoOperationVM): string => {
    return operation.billOfLadingWeight ? `${roundTo3Decimals(operation.billOfLadingWeight)} MT` : '-';
};

/**
 * Format ship figure for display
 * @param operation - The cargo operation view model
 * @param activeStowagePlan - The active stowage plan
 * @returns Formatted ship figure string with MT unit or '-'
 */
export const formatShipFigureDisplay = (operation: RotationCargoOperationVM, activeStowagePlan: StowagePlan): string => {
    const shipFigureValue = calculateShipFigureValue(operation, activeStowagePlan);
    return shipFigureValue === '-' ? '-' : `${shipFigureValue} MT`;
};

/**
 * Format stowed weight for display
 * @param item - Operation with location information
 * @returns Formatted weight string with MT unit or '-' for transit operations
 */
export const formatStowedWeightDisplay = (item: OperationWithLocation): string => {
    return item.operation.operationType === 'Transit' ? '-' : `${roundTo3Decimals(item.stowedWeight.toNumber())} MT`;
};

/**
 * Format not stowed weight for display
 * @param item - Operation with location information
 * @returns Formatted weight string with MT unit or '-'
 */
export const formatNotStowedWeightDisplay = (item: OperationWithLocation): string => {
    if (item.operation.operationType !== 'Loading') return '-';
    if (item.notStowedWeight === null) return '-';
    return `${roundTo3Decimals(item.notStowedWeight.toNumber())} MT`;
};

/**
 * Calculate ship figure value for display
 * NOTE: Business rule verified - Transfer operations return '-' (no ship figure applicable)
 * @param operation - The cargo operation view model
 * @param activeStowagePlan - The active stowage plan
 * @returns Ship figure as string or '-'
 */
export const calculateShipFigureValue = (operation: RotationCargoOperationVM, activeStowagePlan: StowagePlan): string => {
    if (!operation.orgCargoOperation) return '-';

    // Business rule: Transfer operations don't have ship figures
    if (operation.orgCargoOperation.operationType === 'Transfer') return '-';

    // Check if cargo operation has been stowed at all
    const operationTankIds = getCargoOperationStowedTankIds(activeStowagePlan, operation.orgCargoOperation);
    if (operationTankIds.length === 0) {
        return '-';
    }

    const shipFigure = getShipFigureForOperationType(activeStowagePlan, operation.orgCargoOperation);

    // Business rule: Zero ship figure displays as '-'
    if (shipFigure.isZero()) return '-';

    return roundTo3Decimals(shipFigure.toNumber()).toString();
};

/**
 * Get ship figure for a stowage item
 * Use shipFigure if available, otherwise fall back to weight
 * @param stowageItem - The stowage item
 * @returns Formatted ship figure string with MT unit or '-'
 */
export const getStowageItemShipFigure = (stowageItem: Stowage): string => {
    const figureValue =
        stowageItem.shipFigure !== undefined && stowageItem.shipFigure !== null ? stowageItem.shipFigure : stowageItem.weight?.toNumber();

    return figureValue !== undefined && figureValue !== null ? `${roundTo3Decimals(figureValue)} MT` : '-';
};

/**
 * Get warning CSS class for cargo operation based on stowage status
 * @param activeStowagePlan - The active stowage plan
 * @param cargoOperation - The cargo operation
 * @returns Warning class string or empty string
 */
export const getWarningClass = (activeStowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): string => {
    if (cargoOperation.operationType === 'Loading') {
        return roundTo3Decimals(notStowedWeightInTanks(activeStowagePlan, cargoOperation).toNumber()) > 0 ? ' warning' : '';
    }
    // Note: Unloading warning logic not implemented yet
    return '';
};

/**
 * Formats an array of port names as a comma-separated string
 * @param portNames - Array of PortName objects to format
 * @returns Comma-separated string of port names, or empty string if array is empty
 */
export const formatPortNamesAsString = (portNames: PortName[]): string => (portNames?.length ? portNames.map((port) => port.name).join(', ') : '');
